import React, { useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { api } from '../api';

// Greg's public avatar (kept in sync with the seed record in backend/db.py).
const GREG_AVATAR = 'https://customer-assets.emergentagent.com/job_jfk9-ecosystem/artifacts/amt433c5_471147804_10164730467053677_2885523668702348649_n.jpg';

/**
 * MessageGregBubble — a floating chat pill anchored to the bottom-right of the
 * viewport. Clicking it opens a centered Dialog ("chat with Greg") that stays
 * dead-center on every viewport size, so the built-in X close button is
 * always visible and reachable (a common problem with anchored Popovers on
 * narrow mobile screens where Radix collision-detection pushes the popover
 * off to a corner). Submissions reuse the /api/demo-requests endpoint (the
 * notes field is prefixed with [Quick Chat] so Greg can distinguish them).
 */
export const MessageGregBubble = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', message: '' });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please add your name, email, and a short message.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/demo-requests', {
        full_name: form.full_name,
        email: form.email,
        notes: `[Quick Chat] ${form.message}`,
      });
      toast.success('Message sent — Greg will reply within one business day.');
      setForm({ full_name: '', email: '', message: '' });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Trigger — floating pill anchored to the bottom-right corner.
          Sits above the PWA install prompt using env(safe-area-inset)
          on iOS. `fixed` + `z-[70]` keeps it above page content but
          below toasts/Dialog overlays. */}
      <div
        className='fixed z-[70] pointer-events-none'
        style={{
          right: 'max(16px, env(safe-area-inset-right))',
          bottom: 'calc(max(20px, env(safe-area-inset-bottom)) + 96px)',
        }}
        data-testid='message-greg-bubble-wrap'
      >
        <DialogTrigger asChild>
          <button
            type='button'
            className='pointer-events-auto group relative flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-[0_10px_30px_-8px_rgba(11,58,135,0.35)] hover:shadow-[0_16px_40px_-8px_rgba(11,58,135,0.5)] hover:-translate-y-0.5 transition-all'
            data-testid='message-greg-trigger'
            aria-label='Message Greg — quick chat'
          >
            <span className='relative shrink-0'>
              <img
                src={GREG_AVATAR}
                alt='Greg'
                className='w-9 h-9 rounded-full object-cover border-2 border-white dark:border-slate-900 shadow'
                loading='lazy'
              />
              <span
                aria-hidden='true'
                className='absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-slate-900'
                style={{ boxShadow: '0 0 0 0 rgba(16,185,129,0.6)', animation: 'k9-online-pulse 1.8s ease-out infinite' }}
              />
            </span>
            <span className='flex flex-col items-start leading-tight'>
              <span className='text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400'>Message</span>
              <span className='text-sm font-bold text-slate-900 dark:text-slate-50'>Greg</span>
            </span>
            <MessageCircle className='w-4 h-4 text-[#1B3A87] dark:text-[#93C5FD] ml-1' />
          </button>
        </DialogTrigger>
      </div>

      {/* Dialog content — centered on every viewport. The shadcn
          DialogContent already renders a built-in X close in the top
          right corner. Constrain width for mobile (w-[calc(100vw-2rem)])
          and cap height so the form remains scrollable on short
          screens. Zero horizontal padding so the header gradient goes
          edge-to-edge; inner sections handle their own padding. */}
      <DialogContent
        className='w-[calc(100vw-2rem)] sm:max-w-md p-0 gap-0 overflow-hidden max-h-[85dvh] flex flex-col'
        data-testid='message-greg-dialog'
      >
        {/* Header */}
        <div className='relative p-4 pr-10 bg-gradient-to-br from-[#1B3A87] to-[#0B1220] text-white'>
          <div className='flex items-center gap-3'>
            <img
              src={GREG_AVATAR}
              alt='Greg'
              className='w-12 h-12 rounded-full object-cover border-2 border-white/20 shadow-lg'
              loading='lazy'
            />
            <div>
              <div className='font-display font-bold text-base leading-tight'>Chat with Greg</div>
              <div className='flex items-center gap-1.5 text-[11px] text-emerald-300 mt-0.5'>
                <span className='w-1.5 h-1.5 rounded-full bg-emerald-400' /> Usually replies within a day
              </div>
            </div>
          </div>
          <p className='text-[12px] text-slate-200 mt-3 leading-relaxed'>
            Have a quick question? Drop your name, email, and a note &mdash; I&apos;ll get back to you personally.
          </p>
        </div>

        {/* Form — flex-1 + overflow-y-auto so long content is scrollable
            inside the fixed-height dialog on short mobile viewports. */}
        <form
          onSubmit={onSubmit}
          className='p-4 space-y-3 overflow-y-auto flex-1'
          data-testid='message-greg-form'
        >
          <div>
            <Label htmlFor='mg-name' className='text-xs'>Your name *</Label>
            <Input
              id='mg-name'
              value={form.full_name}
              onChange={onChange('full_name')}
              placeholder='Jane Trainer'
              required
              className='h-10'
              data-testid='message-greg-input-name'
            />
          </div>
          <div>
            <Label htmlFor='mg-email' className='text-xs'>Email *</Label>
            <Input
              id='mg-email'
              type='email'
              value={form.email}
              onChange={onChange('email')}
              placeholder='you@yourfacility.com'
              required
              className='h-10'
              data-testid='message-greg-input-email'
            />
          </div>
          <div>
            <Label htmlFor='mg-msg' className='text-xs'>Message *</Label>
            <Textarea
              id='mg-msg'
              rows={3}
              value={form.message}
              onChange={onChange('message')}
              placeholder='Hi Greg — I run a training facility in…'
              required
              className='resize-none'
              data-testid='message-greg-input-message'
            />
          </div>
          <Button
            type='submit'
            disabled={submitting}
            className='w-full bg-[#C8102E] hover:bg-[#A50D26] text-white h-10 font-semibold'
            data-testid='message-greg-submit'
          >
            {submitting ? (
              <>
                <Loader2 className='w-4 h-4 mr-2 animate-spin' /> Sending&hellip;
              </>
            ) : (
              <>
                <Send className='w-4 h-4 mr-2' /> Send Message
              </>
            )}
          </Button>
          <p className='text-[10px] text-slate-400 dark:text-slate-500 text-center pt-0.5'>
            For a full walkthrough, use the demo form on this page instead.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MessageGregBubble;
